import React, { useState, useRef } from 'react';
import { Pencil } from 'lucide-react';

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  isPreview: boolean;
  multiline?: boolean;
  className?: string;
  iconColor?: string;
}

export const EditableText: React.FC<EditableTextProps> = ({
  value,
  onChange,
  isPreview,
  multiline = false,
  className = '',
  iconColor = 'text-blue-400',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const hasFocusedRef = useRef(false);

  // Callback ref to focus and select input when editing starts (only once)
  const inputRefCallback = (element: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (element && !hasFocusedRef.current) {
      element.focus();
      element.select();
      hasFocusedRef.current = true;
    }
  };

  const handleClick = () => {
    if (!isPreview) {
      hasFocusedRef.current = false; // Reset for new editing session
      setIsEditing(true);
      setEditValue(value); // Sync with current value when starting edit
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onChange(editValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (isPreview) {
    return <span className={className}>{value}</span>;
  }

  if (isEditing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRefCallback}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`bg-white bg-opacity-20 border-2 border-yellow-400 rounded px-2 py-1 outline-none resize-none w-full ${className}`}
          rows={2}
        />
      );
    }

    return (
      <input
        ref={inputRefCallback}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`bg-white bg-opacity-20 border-2 border-yellow-400 rounded px-2 py-1 outline-none w-full ${className}`}
      />
    );
  }

  return (
    <span
      onClick={handleClick}
      className={`cursor-pointer hover:bg-white hover:bg-opacity-10 rounded px-2 py-1 inline-flex items-center gap-2 group transition-all ${className}`}
      title="Click to edit"
    >
      {value}
      <Pencil
        className={`w-5 h-5 ${iconColor} opacity-80 group-hover:opacity-100 transition-opacity`}
      />
    </span>
  );
};
